import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../hooks/useWebSocket';
import { useLogStore } from '../state/logStore';
import { useRunStore } from '../state/runStore';
import { useBugReviewStore } from '../state/bugReviewStore';
import { usePreconditionsStore } from '../state/preconditionsStore';
import { useVideosStore } from '../state/videosStore';
import { useGameKbStore } from '../state/gameKbStore';
import { useLookAtThatStore } from '../state/lookAtThatStore';
import { useStakeEngineStore } from '../state/stakeEngineStore';
import { useCriticalAiStore } from '../state/criticalAiStore';
import { useActiveWorkStore } from '../state/activeWorkStore';
import { useAutoWorkflowStore } from '../state/autoWorkflowStore';
import type { WsServerMessage } from '@sr-test-wizard/shared';

/**
 * Always-mounted footer that owns the WS connection. The connection is
 * subscribed to the active runId from the run store so per-run logs and
 * test updates land in this tray + propagate to the run state automatically.
 */
export function GlobalLogTray() {
  const [open, setOpen] = useState(false);
  const push = useLogStore((s) => s.push);
  const entries = useLogStore((s) => s.entries);
  const clear = useLogStore((s) => s.clear);
  const currentRunId = useRunStore((s) => s.current?.id ?? null);
  const setPhase = useRunStore((s) => s.setPhase);
  const applyTestUpdate = useRunStore((s) => s.applyTestUpdate);
  const removeRunLocally = useRunStore((s) => s.removeRunLocally);
  const applyBugUpdate = useBugReviewStore((s) => s.applyBugUpdate);
  const clearBugReview = useBugReviewStore((s) => s.clear);
  const markPreconditionsDirty = usePreconditionsStore((s) => s.markDirty);
  const clearPreconditions = usePreconditionsStore((s) => s.clear);
  const applyExtractProgress = useVideosStore((s) => s.applyExtractProgress);
  const applyAnalyzeProgress = useVideosStore((s) => s.applyAnalyzeProgress);
  const applyNativeStageProgress = useVideosStore((s) => s.applyNativeStageProgress);
  const clearVideos = useVideosStore((s) => s.clear);
  const applyGameKbProgress = useGameKbStore((s) => s.applyProgress);
  const clearGameKb = useGameKbStore((s) => s.clear);
  const loadGameKb = useGameKbStore((s) => s.load);
  const applyAutoWorkflowProgress = useAutoWorkflowStore((s) => s.applyProgress);
  const clearAutoWorkflow = useAutoWorkflowStore((s) => s.clear);
  const loadRun = useRunStore((s) => s.loadRun);
  const loadTexec = useRunStore((s) => s.loadTexec);
  const loadEnvLinks = useRunStore((s) => s.loadEnvLinks);
  const loadVideos = useVideosStore((s) => s.load);
  const applyStakeProgress = useStakeEngineStore((s) => s.applyProgress);
  const clearStake = useStakeEngineStore((s) => s.clear);
  const applyLookAtThat = useLookAtThatStore((s) => s.applyProgress);
  const clearLookAtThat = useLookAtThatStore((s) => s.clear);
  const applyCriticalAiProgress = useCriticalAiStore((s) => s.applyProgress);
  const clearCriticalAi = useCriticalAiStore((s) => s.clear);
  const fetchActiveWork = useActiveWorkStore((s) => s.fetch);
  const navigate = useNavigate();
  const feedRef = useRef<HTMLDivElement>(null);

  const { connected } = useWebSocket(currentRunId, (msg: WsServerMessage) => {
    if (msg.type === 'active-work') {
      void fetchActiveWork();
      return;
    }
    if (msg.type === 'log') {
      push({
        ts: msg.ts,
        level: msg.level,
        message: msg.message,
        runId: msg.runId,
      });
    } else if (msg.type === 'phase') {
      setPhase(msg.phase, msg.status);
      push({
        ts: new Date().toISOString(),
        level: msg.status === 'failed' ? 'error' : msg.status === 'completed' ? 'success' : 'info',
        message: `${msg.phase} -> ${msg.status}${msg.message ? ` (${msg.message})` : ''}`,
        runId: msg.runId,
      });
    } else if (msg.type === 'test-update') {
      applyTestUpdate(msg.testKey, msg.review);
    } else if (msg.type === 'bug-update') {
      applyBugUpdate(msg.bugKey, msg.patch);
    } else if (msg.type === 'precondition-update') {
      // Flip the dirty flag so the active panel (or rail-only preload) refetches.
      markPreconditionsDirty();
    } else if (msg.type === 'video-extract-progress') {
      applyExtractProgress(msg.slug, {
        kind: 'extract',
        current: msg.current,
        total: msg.total,
        completed: msg.current,
        errors: 0,
        status: msg.status,
        message: msg.message,
      });
    } else if (msg.type === 'video-analyze-progress') {
      applyAnalyzeProgress(msg.slug, msg.provider, {
        kind: 'analyze',
        current: msg.current,
        total: msg.total,
        completed: msg.completed,
        errors: msg.errors,
        status: msg.status,
        message: msg.message,
      });
    } else if (msg.type === 'video-native-stage-progress') {
      applyNativeStageProgress(msg.slug, msg.provider, {
        stage: msg.stage,
        status: msg.status,
        message: msg.message,
      });
    } else if (msg.type === 'game-kb-progress') {
      applyGameKbProgress(msg.stage, {
        status: msg.status,
        message: msg.message,
        tool: msg.tool,
      });
    } else if (msg.type === 'stake-engine-progress') {
      applyStakeProgress(msg.stage, {
        status: msg.status,
        message: msg.message,
        tool: msg.tool,
      });
    } else if (msg.type === 'look-at-that-progress') {
      applyLookAtThat(msg);
      if (msg.message && msg.event !== 'screenshot') {
        push({
          ts: new Date().toISOString(),
          level: msg.event === 'error' ? 'error' : 'info',
          message: `Look at that: ${msg.message}`,
          runId: msg.runId,
        });
      }
    } else if (msg.type === 'critical-ai-progress') {
      applyCriticalAiProgress({
        status: msg.status,
        message: msg.message,
        tool: msg.tool,
      });
    } else if (msg.type === 'auto-workflow-progress') {
      applyAutoWorkflowProgress(msg);
      push({
        ts: new Date().toISOString(),
        level:
          msg.status === 'failed' ? 'error' : msg.status === 'completed' ? 'success' : 'info',
        message: `Auto workflow: ${msg.step} → ${msg.status}${msg.message ? ` (${msg.message})` : ''}`,
        runId: msg.runId,
      });
      if (msg.status === 'completed' || msg.status === 'skipped') {
        if (msg.step === 'populate-texec') void loadTexec(msg.runId, { force: true });
        if (msg.step === 'env-links') void loadEnvLinks(msg.runId, { force: true });
        if (
          msg.step === 'pull-source' ||
          msg.step === 'load-tests' ||
          msg.step === 'analyze-tests' ||
          msg.step === 'done'
        ) {
          void loadRun(msg.runId);
        }
        if (msg.step.startsWith('video') || msg.step === 'download-images' || msg.step === 'analyze-images') {
          void loadVideos(msg.runId);
        }
        if (msg.step.startsWith('game-kb')) void loadGameKb(msg.runId);
      }
    } else if (msg.type === 'run-deleted') {
      // Server (or another tab) just nuked this run. If we're currently
      // inside it, jump back to the hub and wipe any per-run scratchpads.
      if (currentRunId === msg.runId) {
        clearBugReview();
        clearPreconditions();
        clearVideos();
        clearGameKb();
        clearAutoWorkflow();
        clearStake();
        clearLookAtThat();
        clearCriticalAi();
        removeRunLocally(msg.runId);
        navigate('/', { replace: true });
      }
      push({
        ts: new Date().toISOString(),
        level: 'info',
        message: `Run ${msg.runId.slice(0, 8)} deleted`,
        runId: null,
      });
    }
  });

  useEffect(() => {
    if (open && feedRef.current) {
      feedRef.current.scrollTop = feedRef.current.scrollHeight;
    }
  }, [entries.length, open]);

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'var(--bg-elev)',
        borderTop: '1px solid var(--border)',
        padding: open ? '8px' : '4px 12px',
        zIndex: 50,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span className={`badge ${connected ? 'good' : 'bad'}`}>
          {connected ? 'WS connected' : 'WS disconnected'}
        </span>
        <span className="muted small">{entries.length} log line(s)</span>
        <span className="spacer" />
        {entries.length > 0 && (
          <button className="btn-ghost btn small" onClick={clear}>
            Clear
          </button>
        )}
        <button className="btn btn-ghost small" onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide log' : 'Show log'}
        </button>
      </div>
      {open && (
        <div ref={feedRef} className="log-feed" style={{ marginTop: 8 }}>
          {entries.map((entry) => (
            <div key={entry.id} className={`l-${entry.level}`}>
              {entry.ts.slice(11, 19)} {entry.message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
